import React, { useMemo, useState } from "react"
import { motion } from "framer-motion"
import { ChevronDown, ChevronUp, CirclePlus } from "lucide-react"
import styles from "./AddCustomMaterialPopup.module.scss"
import ModalOverlay from "../../UI/ModalOverlay/ModalOverlay.tsx"
import CustomSelect from "../../../pages/ProjectDocumentation/components/CustomSelect.tsx"

interface Category {
  id: number | string
  name: string
}

interface CategoriesResponse {
  data?: Category[]
}

interface NewMaterial {
  categoryId: string
  name: string
  unit: string
  price: number
  quantity: number
}

interface AddCustomMaterialPopupProps {
  onClose: () => void
  onAdd: (material: any) => void
  categories: CategoriesResponse
  isEstimacionDeCostos: boolean
}

const variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1 },
}

const UNIT_OPTIONS = [
  { value: "kg", label: "Kilogramos (kg)" },
  { value: "gr", label: "Gramos (gr)" },
  { value: "m", label: "Metros (m)" },
  { value: "cm", label: "Centímetros (cm)" },
  { value: "l", label: "Litros (l)" },
  { value: "ml", label: "Mililitros (ml)" },
  { value: "m2", label: "Metro cuadrado (m²)" },
  { value: "cm2", label: "Centímetro cuadrado (cm²)" },
  { value: "m3", label: "Metro cúbico (m³)" },
  { value: "cm3", label: "Centímetro cúbico (cm³)" },
  { value: "u", label: "Unidad (u)" },
]

const AddCustomMaterialPopup: React.FC<AddCustomMaterialPopupProps> = ({
  onClose,
  onAdd,
  categories,
  isEstimacionDeCostos,
}) => {
  const [newMaterial, setNewMaterial] = useState<NewMaterial>({
    categoryId: "",
    name: "",
    unit: "",
    price: 0,
    quantity: 1,
  })

  const categoryOptions = useMemo(
    () =>
      (categories?.data || []).map((category) => ({
        value: String(category.id),
        label: category.name,
      })),
    [categories?.data],
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setNewMaterial((prev) => ({
      ...prev,
      [name]: name === "price" || name === "quantity" ? Number(value) : value,
    }))
  }

  const handleChangeCategory = (value: string) => {
    setNewMaterial((prev) => ({
      ...prev,
      categoryId: value,
    }))
  }

  const handleChangeUnit = (value: string) => {
    setNewMaterial((prev) => ({
      ...prev,
      unit: value,
    }))
  }

  const handleQuantityChange = (increment: number) => {
    setNewMaterial((prev) => ({
      ...prev,
      quantity: Math.max(0, prev.quantity + increment),
    }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onAdd({
      ...newMaterial,
      id: Date.now().toString(),
      temporary: true,
    })
  }

  const isFormValid = Boolean(newMaterial.categoryId && newMaterial.name && newMaterial.unit && newMaterial.quantity)

  return (
    <ModalOverlay isOpen onClose={onClose} preventClose zIndex={32}>
      <motion.div initial="hidden" animate="visible" variants={variants} className={styles.popup}>
        <div className={styles.popup_header}>
          <h2 className={styles.popup_header_title}>Crear material</h2>
          <button type="button" onClick={onClose} className={styles.popup_header_cancelBtn}>
            Cancelar
          </button>
        </div>
        <form className={styles.popup_form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="categoryId">Categoría</label>
            <CustomSelect
              value={newMaterial.categoryId}
              onChange={handleChangeCategory}
              options={categoryOptions}
              placeholder="Selecciona una categoría"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="name">Nombre</label>
            <input
              id="name"
              name="name"
              type="text"
              value={newMaterial.name}
              onChange={handleChange}
              placeholder="Por ej: Arena fina bolsa x 25kg"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="unit">Unidad</label>
            <CustomSelect
              value={newMaterial.unit}
              onChange={handleChangeUnit}
              options={UNIT_OPTIONS}
              placeholder="Selecciona una unidad"
            />
          </div>

          {isEstimacionDeCostos && (
            <div className={styles.formGroup}>
              <label htmlFor="price">Precio unitario estimado</label>
              <input
                id="price"
                name="price"
                type="number"
                min={0}
                value={newMaterial.price}
                onChange={handleChange}
                placeholder="Por ej: 100"
                required
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="quantity">Cantidad</label>
            <div className={styles.quantityControl}>
              <button type="button" onClick={() => handleQuantityChange(1)} className={styles.quantityButton}>
                <ChevronUp size={18} />
              </button>
              <input
                id="quantity"
                name="quantity"
                type="number"
                min={0}
                value={newMaterial.quantity}
                onChange={handleChange}
              />
              <button type="button" onClick={() => handleQuantityChange(-1)} className={styles.quantityButton}>
                <ChevronDown size={18} />
              </button>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button disabled={!isFormValid} type="submit" className={styles.buttonGroup_acceptBtn}>
              <CirclePlus size={18} />
              Agregar
            </button>
          </div>
        </form>
      </motion.div>
    </ModalOverlay>
  )
}

export default AddCustomMaterialPopup

